import React, { useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  MenuItem,
  Autocomplete,
  Chip,
} from '@mui/material';
import { COLORS } from '../../theme';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAllStores } from '../Query/ManageStoreQuery';
import { primary } from '../../theme';
import {
  useCoupons,
  useCreateCoupon,
  UseEditCouponById,
} from '../Query/CouponQuery';
import { useStoreManagementParams } from './StoreManagement/useStoreManagementParams';

interface FormValues {
  couponCode: string;
  couponName: string;
  couponType: string;
  couponValue: string;
  maximumDiscount: string;
  couponStartDate: string;
  couponEndDate: string;
  restaurants: string[];
  restaurantIds: string[];
  couponPerUserLimit: string;
  totalCouponCount: string;
  minimumOrderValue: string;
  sendEmailToAllUsers: boolean;
}

interface CouponFormProps {
  open: boolean;
  onClose: () => void;
  editData?: FormValues & { id: string };
}

const CouponSchema = Yup.object().shape({
  couponCode: Yup.string()
    .max(10, 'Coupon code must be at most 10 characters')
    .required('Required'),
  couponName: Yup.string()
    .required('Coupon Name Required')
    .test('not-empty-string', 'Coupon Name cannot be empty or contain only spaces', function (value) {
      if (!value) return false;
      return value.trim().length > 0;
    })
    .test('not-only-numbers', 'Coupon Name cannot contain only numbers', function (value) {
      if (!value) return true;
      const trimmedValue = value.trim();
      return !/^\d+$/.test(trimmedValue);
    })
    .test('not-only-special-chars', 'Coupon Name cannot contain only special characters', function (value) {
      if (!value) return true;
      const trimmedValue = value.trim();
      // Check if the string contains only special characters (no letters or numbers)
      return !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/.test(trimmedValue);
    })
    .test('has-valid-content', 'Coupon Name must contain at least one letter', function (value) {
      if (!value) return true;
      const trimmedValue = value.trim();
      // Must contain at least one letter (a-z, A-Z)
      return /[a-zA-Z]/.test(trimmedValue);
    })
    .min(1, 'Coupon Name must be at least 1 character'),
  couponType: Yup.string().required('Coupon Type Required'),
  couponValue: Yup.string()
    .required('Discount value is required')
    .test(
      'is-number',
      'Discount value must be a number',
      (value) => !isNaN(Number(value)) && Number(value) > 0
    )
    .test(
      'percentage-range',
      'For Percentage type, discount percentage must be between 1 and 100',
      function (value) {
        const { couponType } = this.parent as { couponType: string };
        if (!value) return true;
        if (couponType === 'Percentage') {
          const numeric = Number(value);
          if (isNaN(numeric)) return false;
          return numeric >= 1 && numeric <= 100;
        }
        return true;
      }
    ),
  maximumDiscount: Yup.string()
    .when('couponType', {
      is: (val: string) => val === 'Percentage',
      then: (schema) =>
        schema
          .required('Maximum discount amount is required for Percentage type')
          .test(
            'is-number',
            'Maximum discount must be a number',
            (value) => !isNaN(Number(value)) && Number(value) > 0
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  couponStartDate: Yup.string()
    .required('Coupon Start Date Required')
    .test(
      'is-valid-date',
      'Start date must be a valid date',
      (value) => {
        if (!value) return true;
        const date = new Date(value);
        return !isNaN(date.getTime());
      }
    )
    .test(
      'is-not-past',
      'Start date cannot be in the past',
      (value) => {
        if (!value) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const date = new Date(value);
        return date >= today;
      }
    ),
  couponEndDate: Yup.string()
    .required('Coupon End Date Required')
    .test(
      'is-after-start',
      'End date must be after start date',
      function (value) {
        const { couponStartDate } = this.parent;
        if (!couponStartDate || !value) return true;
        return new Date(value) > new Date(couponStartDate);
      }
    ),
  restaurantIds: Yup.array()
    .min(1, 'Please select at least one store')
    .required('Stores Required'),
  couponPerUserLimit: Yup.string()
    .required('Coupon Per User Limit Required')
    .test(
      'is-number',
      'Coupon Per User Limit must be a number',
      (value) => !isNaN(Number(value)) && Number(value) > 0
    ),
  totalCouponCount: Yup.string()
    .required('Total Coupon Count Required')
    .test(
      'is-number',
      'Total Coupon Count must be a number',
      (value) => !isNaN(Number(value)) && Number(value) > 0
    ),
  minimumOrderValue: Yup.string()
    .required('Minimum order value Required')
    .test(
      'is-number',
      'Minimum order value must be a number',
      (value) => !isNaN(Number(value)) && Number(value) > 0
    )
    .test(
      'greater-than-discount',
      'Minimum order value must be greater than discount amount',
      function (value) {
        const { couponType, couponValue } = this.parent;
        if (!value || !couponValue || couponType !== 'Flat') return true;
        const minOrderValue = Number(value);
        const discountValue = Number(couponValue);
        return minOrderValue > discountValue;
      }
    ),
  sendEmailToAllUsers: Yup.boolean().required(
    'Send Email To All Users Required'
  ),
});

const CouponForm = ({ open, onClose, editData }: CouponFormProps) => {
  console.log(open, 'editData');
  const [couponMode, setCouponMode] = React.useState<'auto' | 'custom'>(
    editData ? 'custom' : 'auto'
  );
  const { mutate: CreateCoupon, isPending: CreateUserLoading } =
    useCreateCoupon();
  const { mutate: UpdateCoupon, isPending: UpdateCouponLoading } =
    UseEditCouponById();
  const {
    mutate: generateCoupon,
    //data: generatedData,
    isPending: generating,
  } = useCoupons();

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fieldArray = [
    { name: 'couponName', label: 'Coupon Name', type: 'text' },
    { name: 'couponValue', label: 'Discount Value', type: 'text' }, // changed from 'number' to 'text'
    { name: 'couponStartDate', label: 'Start Date', type: 'date' },
    { name: 'couponEndDate', label: 'End Date', type: 'date' },
    { name: 'couponPerUserLimit', label: 'Coupon per User', type: 'text' }, // changed from 'number' to 'text'
    { name: 'totalCouponCount', label: 'Total Coupons', type: 'text' }, // changed from 'number' to 'text'
    { name: 'minimumOrderValue', label: 'Minimum Order Value', type: 'text' }, // changed from 'number' to 'text'
  ];
  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      width: '90%',
    },
    // Zero out the input's internal padding
    // '& .MuiOutlinedInput-input': {
    //   padding: 0,
    // },
    // // Remove extra right padding when an adornment (clear button) is present
    // '& .MuiInputBase-inputAdornedEnd': {
    //   paddingRight: 0,
    // },
    // For Autocomplete with multiple chips, remove root padding
    '& .MuiAutocomplete-inputRoot': {
      padding: 0,
    },
  };
  const labelStyles = {
    width: { xs: '100%', sm: '180px' },
    color: COLORS.text.primary,
    fontSize: 14,
    fontWeight: 500,
    justifyContent: {
      xs: 'flex-start',
      sm: 'flex-end',
    },
    '& .MuiFormLabel-asterisk': {
      color: 'red',
    },
  };
  const { params } = useStoreManagementParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const {
    data: manageStoreData,
  } = useAllStores(memoizedParams);

  // Keep in sync via Formik effect below (based on IDs)
  useEffect(() => {
    // no-op; handled inside Formik where setFieldValue is available
  }, [editData, manageStoreData?.data]);

  // const initialRestaurantId = React.useMemo(() => {
  //   if (editData?.restaurantIds?.length) {
  //     return editData.restaurantIds[0];
  //   }
  //   return '';
  // }, [editData]);

  // Format dates for initial values
  const formattedStartDate = React.useMemo(() => {
    if (!editData?.couponStartDate) return '';
    try {
      return formatDate(editData.couponStartDate);
    } catch (error) {
      console.error('Error formatting start date:', error);
      return '';
    }
  }, [editData?.couponStartDate]);

  const formattedEndDate = React.useMemo(() => {
    if (!editData?.couponEndDate) return '';
    try {
      return formatDate(editData.couponEndDate);
    } catch (error) {
      console.error('Error formatting end date:', error);
      return '';
    }
  }, [editData?.couponEndDate]);

  const handleGenerate = () =>
    Math.random().toString(36).substring(2, 10).toUpperCase();

  return (
    <Box>
      <DialogContent
        style={{
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '24px',
          backgroundColor: '#fff',
          margin: 15,
        }}
      >
        {/* {(CreateUserLoading || UpdateCouponLoading) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(4px)',
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Loading message={editData ? 'Updating...' : 'Creating...'} />
            </Box>
          )} */}
        <Formik
          enableReinitialize
          initialValues={{
            couponCode: editData?.couponCode || handleGenerate(),
            couponName: editData?.couponName || '',
            couponType: editData?.couponType || '',
            couponValue: editData?.couponValue
              ? Number(editData?.couponValue)
              : '',
            maximumDiscount: (editData as any)?.maxCouponAmount
              ? Number((editData as any)?.maxCouponAmount)
              : (editData as any)?.maximumDiscount
                ? Number((editData as any)?.maximumDiscount)
                : '',
            couponStartDate: formattedStartDate,
            couponEndDate: formattedEndDate,
            restaurants: Array.isArray((editData as any)?.stores)
              ? (editData as any).stores.map((store: any) =>
                typeof store === 'object' && store.storeName ? store.storeName : store
              )
              : (Array.isArray(editData?.restaurants) ? editData!.restaurants : []),
            restaurantIds:
              (Array.isArray((editData as any)?.storeIds) && (editData as any).storeIds.length > 0)
                ? (editData as any).storeIds
                : ((Array.isArray(editData?.restaurantIds) && editData!.restaurantIds.length > 0)
                  ? editData!.restaurantIds
                  : (Array.isArray((editData as any)?.stores)
                    ? (editData as any).stores.map((store: any) =>
                      typeof store === 'object' && store.storeId ? store.storeId : store
                    )
                    : (Array.isArray(editData?.restaurants)
                      ? editData!.restaurants
                      : []
                    )
                  )
                ),
            couponPerUserLimit: editData?.couponPerUserLimit
              ? Number(editData?.couponPerUserLimit)
              : '',
            totalCouponCount: editData?.totalCouponCount
              ? Number(editData.totalCouponCount)
              : '',
            minimumOrderValue: editData?.minimumOrderValue
              ? Number(editData.minimumOrderValue)
              : '',
            sendEmailToAllUsers: editData?.sendEmailToAllUsers ?? true,
          }}
          validationSchema={CouponSchema}
          validateOnMount
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values) => {
            const updatedValues = {
              ...values,
              stores: values.restaurantIds, // send IDs in stores (API expects stores)
              couponValue: Number(values.couponValue),  // Ensure couponValue is a number
              maxCouponAmount: values.couponType === 'Percentage' && values.maximumDiscount !== ''
                ? Number(values.maximumDiscount)
                : undefined,
              couponPerUserLimit: Number(values.couponPerUserLimit),  // Similarly for other numeric fields
              totalCouponCount: Number(values.totalCouponCount),
              minimumOrderValue: Number(values.minimumOrderValue),
            } as any;
            delete updatedValues.restaurantIds; // remove restaurantIds from payload
            delete updatedValues.restaurants; // remove legacy field from payload if present
            if (updatedValues.maxCouponAmount === undefined) delete updatedValues.maxCouponAmount;
            // ensure we don't send UI-only field
            if ('maximumDiscount' in updatedValues) delete updatedValues.maximumDiscount;
            console.log('Form Values:', updatedValues);
            if (editData) {
              UpdateCoupon(
                { id: editData.id, data: updatedValues },
                {
                  onSuccess: () => onClose(),
                  onError: (error: any) =>
                    console.error('UpdateCoupon error:', error),
                }
              );
            } else {
              CreateCoupon(updatedValues, {
                onSuccess: () => onClose(),
                onError: (error: any) =>
                  console.error('CreateCoupon error:', error),
              });
            }
          }}
        >
          {({
            values,
            handleChange,
            setFieldValue,
            errors,
            touched,
            dirty,
            setFieldTouched,
          }) => {
            // Sync selections in edit mode based on IDs when store list loads
            useEffect(() => {
              if (editData && manageStoreData?.data) {
                const sourceIds = (Array.isArray((editData as any).storeIds) && (editData as any).storeIds.length > 0)
                  ? (editData as any).storeIds
                  : ((Array.isArray(editData.restaurantIds) && editData.restaurantIds.length > 0)
                    ? editData.restaurantIds
                    : (Array.isArray((editData as any).stores)
                      ? (editData as any).stores.map((store: any) =>
                        typeof store === 'object' && store.storeId ? store.storeId : store
                      )
                      : (Array.isArray(editData.restaurants) ? editData.restaurants : [])
                    )
                  );
                if (!Array.isArray(sourceIds)) return;
                const selected = manageStoreData.data.filter(store => sourceIds.includes(store.id));
                const selectedNames = selected.map(s => s.nameEnglish);
                const selectedIds = selected.map(s => s.id);
                const namesChanged = JSON.stringify(values.restaurants) !== JSON.stringify(selectedNames);
                const idsChanged = JSON.stringify(values.restaurantIds) !== JSON.stringify(selectedIds);
                if (namesChanged) setFieldValue('restaurants', selectedNames, false);
                if (idsChanged) setFieldValue('restaurantIds', selectedIds, false);
              }
            }, [editData, manageStoreData?.data]);
            return (
              <Form>
                {/* Coupon Mode */}
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                  <Box sx={{ pr: '50px', display: 'flex', alignItems: 'center' }}>
                    <Typography sx={labelStyles}>Coupon Code Mode</Typography>
                  </Box>
                  <RadioGroup
                    row
                    value={couponMode}
                    onChange={(e) => {
                      const mode = e.target.value as 'auto' | 'custom';
                      setCouponMode(mode);
                      if (mode === 'auto') {
                        setFieldValue('couponCode', handleGenerate());
                      }
                    }}
                  >
                    <FormControlLabel
                      value="auto"
                      control={<Radio />}
                      label="Auto Generate"
                    />
                    <FormControlLabel
                      value="custom"
                      control={<Radio />}
                      label="Custom"
                    />
                  </RadioGroup>
                </Box>

                {/* Coupon Code */}
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                  <Box sx={{ width: '25%' }}>
                    <Typography sx={labelStyles}>
                      Coupon Code <span style={{ color: 'red' }}>*</span>
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      name="couponCode"
                      value={values.couponCode}
                      disabled={couponMode === 'auto'}
                      onChange={handleChange}
                      error={touched.couponCode && Boolean(errors.couponCode)}
                      helperText={touched.couponCode && errors.couponCode}
                      sx={inputStyles}
                    // inputProps={{ maxLength: 10 }}
                    />
                    {couponMode === 'auto' && (
                      <Button
                        variant="contained"
                        onClick={() => {
                          generateCoupon(undefined, {
                            onSuccess: (data: { couponCode: any }) => {
                              if (data?.couponCode) {
                                setFieldValue('couponCode', data.couponCode);
                              }
                            },
                          });
                        }}
                        sx={{
                          backgroundColor: '#f44336',
                          color: '#fff',
                          minWidth: '100px',
                          textTransform: 'none',
                        }}
                        disabled={generating}
                      >
                        {generating ? 'Generating...' : 'Generate'}
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Coupon Name */}
                {(() => {
                  const name = 'couponName';
                  const label = 'Coupon Name';
                  const type = 'text';
                  return (
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '33%' }}>
                        <Typography sx={labelStyles}>
                          {label} <span style={{ color: 'red' }}>*</span>
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        name={name}
                        type={type}
                        value={(values as any)[name]}
                        onChange={(e) => {
                          const value = e.target.value;
                          console.log(value, 'name');
                          handleChange(e);
                          setFieldTouched(name, true, true);
                        }}
                        onBlur={(e) => {
                          console.log(e, 'name');
                          const trimmedValue = e.target.value.trim();
                          if (trimmedValue !== e.target.value) {
                            setFieldValue(name, trimmedValue, true);
                          }
                          setFieldTouched(name, true, true);
                        }}
                        error={Boolean((touched as any)[name] && (errors as any)[name])}
                        helperText={(touched as any)[name] ? (errors as any)[name] : undefined}
                        sx={inputStyles}
                      />
                    </Box>
                  );
                })()}

                {/* Coupon Type (moved above Value) */}
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                  <Box sx={{ width: '33%' }}>
                    <Typography sx={labelStyles}>
                      Type <span style={{ color: 'red' }}>*</span>
                    </Typography>
                  </Box>
                  <TextField
                    select
                    fullWidth
                    name="couponType"
                    value={values.couponType}
                    onChange={handleChange}
                    onBlur={(e) => {
                      console.log(e, 'name');
                      setFieldTouched('couponType', true, true);
                    }}
                    error={touched.couponType && Boolean(errors.couponType)}
                    helperText={touched.couponType && errors.couponType}
                    sx={inputStyles}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Percentage">Percentage</MenuItem>
                    <MenuItem value="Flat">Flat</MenuItem>
                  </TextField>
                </Box>

                {/* Value (placed just below Type) */}
                {(() => {
                  const name = 'couponValue';
                  const label = values.couponType === 'Percentage' ? 'Discount Percentage' : 
                                values.couponType === 'Flat' ? 'Discount Amount' : 'Value';
                  const type = 'text';
                  return (
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '33%' }}>
                        <Typography sx={labelStyles}>
                          {label} <span style={{ color: 'red' }}>*</span>
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        name={name}
                        type={type}
                        value={(values as any)[name]}
                        onChange={(e) => {
                          handleChange(e);
                        }}
                        onBlur={(e) => {
                          console.log(e, 'name');
                          setFieldTouched(name, true, true);
                        }}
                        error={Boolean((touched as any)[name] && (errors as any)[name])}
                        helperText={(touched as any)[name] ? (errors as any)[name] : undefined}
                        sx={inputStyles}
                        inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }}
                        onKeyPress={(e) => {
                          const isDigit = /\d/.test(e.key);
                          const isDot = e.key === '.';
                          if (!isDigit && !isDot) {
                            e.preventDefault();
                            return;
                          }
                          if (isDot) {
                            const current = String((values as any)[name] ?? '');
                            if (current.includes('.')) {
                              e.preventDefault();
                            }
                          }
                        }}
                      />
                    </Box>
                  );
                })()}

                {/* Maximum Discount (only for Percentage) */}
                {values.couponType === 'Percentage' && (
                  (() => {
                    const name = 'maximumDiscount';
                    const label = 'Maximum Discount Amount';
                    const type = 'text';
                    return (
                      <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                        <Box sx={{ width: '33%' }}>
                          <Typography sx={labelStyles}>
                            {label} <span style={{ color: 'red' }}>*</span>
                          </Typography>
                        </Box>
                        <TextField
                          fullWidth
                          name={name}
                          type={type}
                          value={(values as any)[name]}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={() => {
                            setFieldTouched(name, true, true);
                          }}
                          error={Boolean((touched as any)[name] && (errors as any)[name])}
                          helperText={(touched as any)[name] ? (errors as any)[name] : undefined}
                          sx={inputStyles}
                          inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }}
                          onKeyPress={(e) => {
                            const isDigit = /\d/.test(e.key);
                            const isDot = e.key === '.';
                            if (!isDigit && !isDot) {
                              e.preventDefault();
                              return;
                            }
                            if (isDot) {
                              const current = String((values as any)[name] ?? '');
                              if (current.includes('.')) {
                                e.preventDefault();
                              }
                            }
                          }}
                        />
                      </Box>
                    );
                  })()
                )}

                {/* Remaining Text Fields */}
                {fieldArray
                  .filter(({ name }) => name !== 'couponName' && name !== 'couponValue')
                  .map(({ name, label, type }) => (
                    <Box
                      key={name}
                      sx={{ display: 'flex', mb: 2, alignItems: 'center' }}
                    >
                      <Box sx={{ width: '33%' }}>
                        <Typography sx={labelStyles}>
                          {label} <span style={{ color: 'red' }}>*</span>
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        name={name}
                        type={type}
                        value={(values as any)[name]}
                        onChange={(e) => {
                          handleChange(e);
                          if (name === 'couponStartDate' || name === 'couponEndDate') {
                            setFieldTouched(name, true, true);
                          }
                        }}
                        onBlur={(e) => {
                          console.log(e, 'name');
                          setFieldTouched(name, true, true);
                        }}
                        error={Boolean(
                          (touched as any)[name] && (errors as any)[name]
                        )}
                        helperText={
                          (touched as any)[name] &&
                          ((name === 'couponStartDate' && (values as any)[name] && (errors as any)[name] === 'Coupon Start Date Required')
                            ? ''
                            : (name === 'couponEndDate' && (values as any)[name] && (errors as any)[name] === 'Coupon End Date Required')
                              ? ''
                              : (errors as any)[name])
                        }
                        InputLabelProps={
                          type === 'date'
                            ? { shrink: true }
                            : undefined
                        }
                        sx={inputStyles}
                        inputProps={
                          name === 'couponPerUserLimit' || name === 'totalCouponCount'
                            ? { inputMode: 'numeric', pattern: '[0-9]*' }
                            : name === 'minimumOrderValue'
                            ? { inputMode: 'decimal', pattern: '[0-9.]*' }
                            : type === 'date'
                            ? { 
                                min: new Date().toISOString().split('T')[0],
                                style: { cursor: 'pointer' }
                              }
                            : undefined
                        }
                        onKeyPress={
                          name === 'couponPerUserLimit' || name === 'totalCouponCount'
                            ? (e) => {
                              if (!/\d/.test(e.key)) {
                                e.preventDefault();
                              }
                            }
                            : name === 'minimumOrderValue'
                            ? (e) => {
                              const isDigit = /\d/.test(e.key);
                              const isDot = e.key === '.';
                              if (!isDigit && !isDot) {
                                e.preventDefault();
                                return;
                              }
                              if (isDot) {
                                const current = String((values as any)[name] ?? '');
                                if (current.includes('.')) {
                                  e.preventDefault();
                                }
                              }
                            }
                            : undefined
                        }
                        onClick={
                          type === 'date'
                            ? (e) => {
                              const input = e.target as HTMLInputElement;
                              if (input && input.showPicker) {
                                input.showPicker();
                              }
                            }
                            : undefined
                        }
                        onFocus={
                          type === 'date'
                            ? (e) => {
                              const input = e.target as HTMLInputElement;
                              if (input && input.showPicker) {
                                input.showPicker();
                              }
                            }
                            : undefined
                        }
                      />
                    </Box>
                  ))}

                {/* Restaurants */}
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{
                      ...labelStyles,   // keep your existing styles
                      mr: '14px',       // add margin-right
                    }}>
                      Store Name(s) <span style={{ color: 'red' }}>*</span>
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {/* <Autocomplete
                      multiple
                      options={manageStoreData?.data || []}
                      getOptionLabel={(option) => option.nameEnglish}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={(manageStoreData?.data || []).filter(store => (values.restaurantIds || []).includes(store.id))}
                      onChange={(_, newValues) => {
                        const uniqueById = Array.from(new Map((newValues || []).map(s => [s.id, s])).values());
                        const names = uniqueById.map(s => s.nameEnglish);
                        const ids = uniqueById.map(s => s.id);
                        setFieldValue('restaurants', names);
                        setFieldValue('restaurantIds', ids);
                      }}
                      disableCloseOnSelect
                      onBlur={() => {
                        setFieldTouched('restaurantIds', true, true);
                      }}
                      
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={touched.restaurantIds && Boolean(errors.restaurantIds)}
                          helperText={
                            touched.restaurantIds
                              ? (typeof errors.restaurantIds === 'string' ? errors.restaurantIds : undefined)
                              : undefined
                          }
                          placeholder="--Select Stores--"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              width: '90%',
                            },
                            mb: 2, // 👈 margin-bottom (adjust the value as needed)
                          }}
                        />
                      )}
                    /> */}
                    <Autocomplete
                      size="small"
                      multiple
                      options={manageStoreData?.data || []}
                      getOptionLabel={(option) => option.nameEnglish}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={(manageStoreData?.data || []).filter(store => (values.restaurantIds || []).includes(store.id))}
                      onChange={(_, newValues) => {
                        const uniqueById = Array.from(new Map((newValues || []).map(s => [s.id, s])).values());
                        const names = uniqueById.map(s => s.nameEnglish);
                        const ids = uniqueById.map(s => s.id);
                        setFieldValue('restaurants', names);
                        setFieldValue('restaurantIds', ids);
                      }}
                      disableCloseOnSelect
                      onBlur={() => {
                        setFieldTouched('restaurantIds', true, true);
                      }}
                      renderTags={() => null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={touched.restaurantIds && Boolean(errors.restaurantIds)}
                          helperText={
                            touched.restaurantIds
                              ? (typeof errors.restaurantIds === 'string' ? errors.restaurantIds : undefined)
                              : undefined
                          }
                          placeholder="--Select Stores--"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              width: '90%',
                              minHeight: 40,
                              alignItems: 'center',
                              paddingTop: 0.5,
                              paddingBottom: 0.5,
                            },
                            '& .MuiAutocomplete-inputRoot': {
                              padding: '2px !important',
                              flexWrap: 'nowrap !important',
                            },
                            '& .MuiAutocomplete-input': {
                              padding: '4px 6px !important',
                              lineHeight: '24px',
                            },
                            mb: 2,
                          }}
                        />
                      )}
                    />
                    {/* Render selected chips below the input */}
                    <Box sx={{
                      width: '90%',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: -1,
                      mb: 2,
                    }}>
                      {(manageStoreData?.data || [])
                        .filter(store => (values.restaurantIds || []).includes(store.id))
                        .map(store => (
                          <Chip
                            key={store.id}
                            label={store.nameEnglish}
                            onDelete={() => {
                              const ids = (values.restaurantIds || []).filter((id: string) => id !== store.id);
                              const names = (values.restaurants || []).filter((n: string) => n !== store.nameEnglish);
                              setFieldValue('restaurantIds', ids);
                              setFieldValue('restaurants', names);
                            }}
                            size="small"
                          />
                        ))}
                    </Box>
                  </Box>
                </Box>

                {/* Send Email */}
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                  <Box sx={{ pr: '18px' }}>
                    <Typography sx={labelStyles}>
                      Send Email to All Users{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </Typography>
                  </Box>
                  <RadioGroup
                    row
                    name="sendEmailToAllUsers"
                    value={values.sendEmailToAllUsers.toString()}
                    onChange={(e) => {
                      const value = e.target.value === 'true';
                      setFieldValue('sendEmailToAllUsers', value);
                    }}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Enable"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="Disable"
                    />
                  </RadioGroup>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', mt: 4 }}>
                  <Box sx={{ width: '25%' }} />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={
                        editData
                          ? !dirty || CreateUserLoading || UpdateCouponLoading
                          : CreateUserLoading || UpdateCouponLoading
                      }
                      sx={{ bgcolor: primary.main, textTransform: 'none' }}
                    >
                      {editData
                        ? UpdateCouponLoading
                          ? 'Updating...'
                          : 'Update'
                        : CreateUserLoading
                          ? 'Adding...'
                          : 'Add'}
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#f44336',
                        color: '#fff',
                        textTransform: 'none',
                      }}
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Box>
  );
};

export default CouponForm;
